/**
 * Entity schemas (content-schema §2–3, §5, §7–9). Every entity derives from the
 * primitives in {@link ./primitives} so the whole content vocabulary is one
 * closed set. TS types are inferred from these schemas — the single source of
 * truth (`z.infer`).
 */
import { z } from 'zod';
import {
  AmountSchema,
  ConditionSchema,
  CurveSchema,
  EffectSchema,
  IdSchema,
} from './primitives.js';

/** Fields shared by every content entity (content-schema §1). */
const baseEntity = {
  id: IdSchema,
  name: z.string().min(1),
  description: z.string().optional(),
  icon: z.string().optional(),
  /** Art-generation prompt for the asset pipeline (see `art/README.md`): a detailed,
   * in-house-style description of the entity's graphic. Authoring metadata only — the
   * engine never reads it; it drives the art runner and is safe to omit. */
  prompt: z.string().optional(),
  /** What Glorp says the first time the player buys/activates this entity — the
   * narrator's line for the moment, not a description. The engine never reads it;
   * the narration UI surfaces it on first purchase/activation (safe to omit). */
  narration: z.string().optional(),
  unlock: ConditionSchema.optional(),
};

// ── Resources (§3) ────────────────────────────────────────────────────────────

export const ResourceSchema = z
  .object({
    ...baseEntity,
    age: IdSchema,
    precision: z.enum(['big', 'int']).default('big'),
  })
  .strict();

// ── Generators & converters (§4) ───────────────────────────────────────────────

const YieldSchema = z
  .object({ resource: IdSchema, baseRatePerSec: AmountSchema })
  .strict();

export const GeneratorSchema = z
  .object({
    ...baseEntity,
    age: IdSchema,
    produces: z.array(YieldSchema).min(1),
    /** Present on converters (e.g. Split: Nutrients → Cells); halts on empty inputs. */
    consumes: z.array(YieldSchema).optional(),
    cost: z.object({ resource: IdSchema, curve: CurveSchema }).strict(),
  })
  .strict();

// ── Upgrades & traits (§5) ──────────────────────────────────────────────────────

export const UpgradeSchema = z
  .object({
    ...baseEntity,
    age: IdSchema,
    cost: z.object({ resource: IdSchema, curve: CurveSchema }).strict(),
    effects: z.array(EffectSchema).min(1),
  })
  .strict();

export const TraitSchema = z
  .object({
    ...baseEntity,
    age: IdSchema,
    /** Keystones survive Age Prestige and gate the age goal. */
    keystone: z.boolean().default(false),
    effects: z.array(EffectSchema).min(1),
  })
  .strict();

// ── Breakthroughs (§7) ──────────────────────────────────────────────────────────

const RewardPoolEntrySchema = z
  .object({ trait: IdSchema, weight: z.number().positive() })
  .strict();

const RewardsSchema = z
  .object({
    mode: z.enum(['randomFromPool', 'chooseFrom', 'fixedOrder']),
    chooseCount: z.number().int().positive().optional(),
    pool: z.array(RewardPoolEntrySchema).optional(),
    exhaustible: z.boolean().optional(),
    /** The age-boss roll: its "reward" is completing the age (§7). */
    ageComplete: z.boolean().optional(),
  })
  .strict()
  .refine((r) => r.ageComplete === true || (r.pool !== undefined && r.pool.length > 0), {
    message: 'rewards must grant a non-empty pool or set ageComplete: true',
  });

export const BreakthroughSchema = z
  .object({
    ...baseEntity,
    name: z.string().min(1).optional(),
    age: IdSchema,
    cost: z.array(z.object({ resource: IdSchema, amount: AmountSchema }).strict()).min(1),
    costCurve: CurveSchema.optional(),
    baseChance: z.number().min(0).max(1),
    pity: z
      .object({ incrementPerFail: z.number().min(0), resetOnHit: z.boolean() })
      .strict()
      .optional(),
    rewards: RewardsSchema,
  })
  .strict();

// ── Milestones (§8) ─────────────────────────────────────────────────────────────

export const MilestoneSchema = z
  .object({
    ...baseEntity,
    name: z.string().min(1).optional(),
    condition: ConditionSchema,
    effects: z.array(EffectSchema).default([]),
    toast: z.string().optional(),
    /** Achievements reuse the milestone vocabulary (GAM-25): flagged entries surface
     * in the achievements panel rather than acting as silent unlock thresholds. */
    achievement: z.boolean().optional(),
  })
  .strict();

// ── Ages (§2) ───────────────────────────────────────────────────────────────────

const ClickActionSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1),
    grants: z
      .array(z.object({ resource: IdSchema, amount: AmountSchema }).strict())
      .min(1),
  })
  .strict();

const AgeGoalSchema = z
  .object({
    type: z.literal('breakthroughHits'),
    breakthrough: IdSchema,
    hits: z.number().int().positive(),
  })
  .strict();

/** One sprite layer of the age's mouse-driven parallax backdrop. Each layer is a
 * distinct generated asset (a transparent cutout), so it carries its own art prompt. */
const BackgroundSpriteSchema = z
  .object({
    id: IdSchema,
    /** Depth: the base plate sits at z 0; higher z is nearer the camera and gets a
     * larger mouse-parallax offset (the render layer maps z → drift speed). */
    z: z.number().int().positive(),
    prompt: z.string().min(1),
  })
  .strict();

/** The age's parallax backdrop: the age's own `prompt` already renders the full-bleed
 * scene image (§2 `scene` art kind), so `background` only adds the foreground sprite
 * layers stacked above it, plus a `prompt` describing that backdrop for future sprite
 * generation (art/README.md — no output of its own; combined with `uiPrompt` and each
 * sprite's own prompt so Codex sees artstyle + background + subject together).
 * Authoring metadata for the art pipeline — the engine never reads it. */
const BackgroundSchema = z
  .object({
    prompt: z.string().min(1),
    sprites: z.array(BackgroundSpriteSchema).min(1),
  })
  .strict();

export const AgeSchema = z
  .object({
    ...baseEntity,
    arc: z.enum(['life', 'civilization', 'cosmos']),
    order: z.number().int().positive(),
    scene: z.string().min(1),
    /** Art-generation prompt for the age's UI chrome: one style description that drives
     * the age's 9-slice frame set (panels, buttons — corners drawn 1:1, edges/centre
     * tile). Optional here so minimal engine-test packs stay lean, but the content-pack
     * linter (`validateContent`) requires it on every shipped age. */
    uiPrompt: z.string().min(1).optional(),
    /** The age's mouse-parallax backdrop (base plate + z-layered sprites). Same
     * requiredness split as `uiPrompt`: optional in the schema, required by the linter. */
    background: BackgroundSchema.optional(),
    clickAction: ClickActionSchema,
    goal: AgeGoalSchema,
    completionExport: z
      .array(z.object({ resource: IdSchema, ratePerSec: AmountSchema }).strict())
      .optional(),
  })
  .strict();

// ── Prestige layers (§9, live in global.json) ─────────────────────────────────────

const ResourceLifetimeMetricSchema = z
  .object({
    type: z.literal('resourceLifetime'),
    resource: IdSchema.optional(),
    resourcePerAge: z.record(IdSchema, IdSchema).optional(),
  })
  .strict()
  .refine((m) => m.resource !== undefined || m.resourcePerAge !== undefined, {
    message: 'gain.metric needs either a resource or a resourcePerAge map',
  });

/** Age-weighted metric (GAM-11): every resource's lifetime scaled by
 * `weightBase^(ageOrder-1)`, so pushing one age deeper always beats grinding a
 * cleared one (game-design §5). */
const AgeWeightedMetricSchema = z
  .object({
    type: z.literal('ageWeighted'),
    weightBase: z.number().gt(1),
  })
  .strict();

const PrestigeGainSchema = z
  .object({
    metric: z.union([ResourceLifetimeMetricSchema, AgeWeightedMetricSchema]),
    curve: CurveSchema,
  })
  .strict();

export const PrestigeLayerSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1),
    scope: z.enum(['currentAge', 'everything']),
    keeps: z.array(z.string().min(1)).default([]),
    /** The layer is unavailable until the player has reached an age of this order
     * (a lifetime stat, so it stays unlocked across full resets — GAM-11). */
    unlockAgeOrder: z.number().int().min(1).optional(),
    currency: z
      .object({
        perAge: z.boolean().optional(),
        names: z.record(IdSchema, z.string().min(1)).optional(),
        name: z.string().min(1).optional(),
      })
      .strict(),
    gain: PrestigeGainSchema,
    /** Prestige-tree upgrades are costed in the layer currency, not a resource id. */
    tree: z
      .array(
        UpgradeSchema.omit({ cost: true }).extend({
          cost: z
            .object({ resource: z.string().min(1), curve: CurveSchema })
            .strict()
            .optional(),
          /** Tree upgrades are eternal, not per-age, so `age` is optional here. */
          age: IdSchema.optional(),
        }),
      )
      .default([]),
  })
  .strict();

// ── Endgame (§11, only the final age ships one — GAM-24) ──────────────────────────
// The narrative close of a universe: the "Seed a New Universe" ceremony (which fires
// the Transcend prestige owned by GAM-11), the lifetime stat readout, and the future
// content branches. This is descriptive surface — the engine reads `ceremony.prestige`
// to know which layer completing the age triggers; the rest drives presentation.

const CeremonyBeatSchema = z
  .object({ text: z.string().min(1), hold: z.number().positive() })
  .strict();

const CeremonySchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1),
    scene: z.string().min(1),
    /** The age-boss breakthrough whose success runs the ceremony. */
    triggeredBy: IdSchema,
    /** The prestige layer completing the age triggers (GAM-11's Transcend). */
    prestige: IdSchema,
    payout: z.enum(['maximum', 'current']),
    essenceBonusMul: AmountSchema,
    credits: z
      .object({
        beats: z.array(CeremonyBeatSchema).min(1),
        closing: z.string().min(1),
        onComplete: z
          .object({
            startNewRun: z.boolean(),
            carry: z.enum(['keepsakes', 'nothing']),
          })
          .strict(),
      })
      .strict(),
  })
  .strict();

/** A lifetime stat surfaced on the endgame screen. `source` is a small tagged union:
 * currency/layer/event references are resolved against the prestige registry (§9). */
const EndgameStatSchema = z
  .object({
    id: IdSchema,
    name: z.string().min(1),
    description: z.string().optional(),
    source: z.union([
      z.object({ type: z.literal('prestigeCount'), layer: IdSchema }).strict(),
      z.object({ type: z.literal('recordAgeOrder') }).strict(),
      z.object({ type: z.literal('lifetimeCurrency'), currency: IdSchema }).strict(),
      z.object({ type: z.literal('lifetimeBreakthroughHits') }).strict(),
      z.object({ type: z.literal('recordRunDuration'), event: IdSchema }).strict(),
    ]),
    format: z.enum(['int', 'big', 'duration']),
  })
  .strict();

/** A post-Transcend content branch (game-design §10 Q4): an exclusive, Essence-priced
 * choice that reshapes future runs. Costed in the eternal Transcend currency, addressed
 * by its layer id — not an age resource. Effects use the standard effect-target registry. */
const ContentBranchSchema = z
  .object({
    ...baseEntity,
    cost: z.array(z.object({ currency: IdSchema, amount: AmountSchema }).strict()).min(1),
    exclusiveGroup: z.string().min(1),
    effects: z.array(EffectSchema).min(1),
  })
  .strict();

export const EndgameSchema = z
  .object({
    id: IdSchema,
    description: z.string().optional(),
    ceremony: CeremonySchema,
    stats: z.array(EndgameStatSchema).default([]),
    contentBranches: z.array(ContentBranchSchema).default([]),
  })
  .strict();

// ── Packs ────────────────────────────────────────────────────────────────────────

export const PackSchema = z
  .object({
    age: AgeSchema,
    resources: z.array(ResourceSchema).min(1),
    generators: z.array(GeneratorSchema).default([]),
    upgrades: z.array(UpgradeSchema).default([]),
    traits: z.array(TraitSchema).default([]),
    breakthroughs: z.array(BreakthroughSchema).min(1),
    milestones: z.array(MilestoneSchema).default([]),
    /** Only the final age ships an endgame block (GAM-24). */
    endgame: EndgameSchema.optional(),
  })
  .strict();

export const GlobalPackSchema = z
  .object({
    prestige: z.array(PrestigeLayerSchema).default([]),
    milestones: z.array(MilestoneSchema).default([]),
  })
  .strict();

export type Resource = z.infer<typeof ResourceSchema>;
export type Generator = z.infer<typeof GeneratorSchema>;
export type Upgrade = z.infer<typeof UpgradeSchema>;
export type Trait = z.infer<typeof TraitSchema>;
export type Breakthrough = z.infer<typeof BreakthroughSchema>;
export type Milestone = z.infer<typeof MilestoneSchema>;
export type Age = z.infer<typeof AgeSchema>;
export type AgeBackground = z.infer<typeof BackgroundSchema>;
export type PrestigeLayer = z.infer<typeof PrestigeLayerSchema>;
export type Endgame = z.infer<typeof EndgameSchema>;
export type Pack = z.infer<typeof PackSchema>;
export type GlobalPack = z.infer<typeof GlobalPackSchema>;

/** Pre-parse (JSON-shaped) pack types — before Zod defaults are applied. */
export type PackInput = z.input<typeof PackSchema>;
export type GlobalPackInput = z.input<typeof GlobalPackSchema>;
